import React, { useContext } from 'react';

import { Badge, Link } from '@ergeon/core-components';
import { PhoneArrowDownLeftIcon, PhoneArrowUpRightIcon } from '@ergeon/icons/hero/micro';
import clsx from 'classnames';
import { Call } from '@twilio/voice-sdk';

import useCallControls from '../../hooks/useCallControls';
import { formatSecondsToTimeString } from '../../utils';
import { TwilioContext } from '../../context-providers/TwilioContext';
import useFetch from '../../hooks/useFetch';
import { fetchCustomerData } from '../../api';
import { GetCustomerAPIDataType, CallWithDuration } from '../../types';
import { formatPhoneNumber } from '../Customers/utils/formatUtils';
import { ROUTES } from '../../routes';

const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const getContainerStateClasses = (isSelected: boolean, isActive: boolean, className?: string) => {
  return clsx(
    'flex flex-col gap-2 p-4 rounded-md outline outline-1 -outline-offset-1 transition-all duration-100 cursor-pointer',
    {
      'cursor-auto bg-blue-100 dark:bg-blue-900 dark:hover:bg-blue-900 hover:bg-blue-100 outline-blue-200 dark:outline-blue-600':
        isSelected,
      'hover:bg-gray-100 dark:hover:bg-gray-700 outline-transparent': !isSelected && !isActive,
      'bg-gray-100 dark:bg-gray-700 outline-gray-300 dark:outline-gray-600': isActive && !isSelected,
    },
    className,
  );
};

const getIconStateClasses = (isSelected: boolean) => {
  return clsx('size-5 shrink-0', {
    'text-blue-500 dark:text-blue-600': isSelected,
    'text-gray-400 dark:text-gray-600': !isSelected,
  });
};

const CallListItem = ({ call, selectedCallId, className }: {call: Call; selectedCallId?: number; className?: string}) => {
  const { getCustomerPhoneNumber, status, callDuration } = useCallControls(call);

  const { setCurrentCall, currentCall, setSelectedHistoricalCall } = useContext(TwilioContext);
  const { data } = useFetch<GetCustomerAPIDataType>(() => fetchCustomerData(getCustomerPhoneNumber()), [currentCall]);

  const customer = data?.customer;

  const isActive = ['connecting', 'open', 'reconnecting', 'ringing', 'pending'].includes(status || '');

  const dbCallId = (call as CallWithDuration).dbCallId;

  const href = dbCallId ?
    ROUTES.call.$buildPath({ params: { callId: dbCallId } }) :
    ROUTES.callNew.$buildPath({ searchParams: { sid: call.parameters?.CallSid || '' } });

  const isSelectedByUrl = selectedCallId !== undefined && dbCallId === selectedCallId;
  const isSelectedByCurrent = !!(currentCall && currentCall.parameters?.CallSid === call.parameters?.CallSid);
  const isSelected = isSelectedByUrl || isSelectedByCurrent;

  const handleSelectCall = () => {
    setCurrentCall(call);
    setSelectedHistoricalCall(undefined); // Clear historical call selection
  };

  const isWarmTransfer = call.customParameters?.get('warm_transfer') === 'True';

  const badgeProps = isWarmTransfer ?
    { flavor: 'warning' as const, value: 'Warm transfer' } :
    { flavor: (isSelected ? 'primary' : 'regular') as const, value: capitalizeFirstLetter(status) };

  return (
    <Link
      className={getContainerStateClasses(isSelected, isActive, className)}
      href={href}
      onClick={handleSelectCall}
      unstyled>
      <div className="flex items-start gap-2 w-full">
        <div className="relative shrink-0">
          {call.direction === Call.CallDirection.Incoming ? (
            <>
              <PhoneArrowDownLeftIcon className={getIconStateClasses(isSelected)} data-testid="incoming-call-icon" />
              {isActive && (
                <PhoneArrowDownLeftIcon
                  className={clsx('top-0 left-0 absolute animate-ping', getIconStateClasses(isSelected))} />
              )}
            </>
          ) : (
            <>
              <PhoneArrowUpRightIcon className={getIconStateClasses(isSelected)} data-testid="outgoing-call-icon" />
              {isActive && (
                <PhoneArrowUpRightIcon
                  className={clsx('top-0 left-0 absolute animate-ping', getIconStateClasses(isSelected))} />
              )}
            </>
          )}
        </div>

        <div className="flex justify-between items-center gap-2 w-full">
          <div className="flex flex-col max-w-44">
            {customer && customer.id && (
              <p className="block max-w-min truncate paragraph-2 text-gray-900 dark:text-white">
                {customer.full_name}
              </p>
            )}
            <div className="flex flex-col gap-1 w-full">
              <p className="font-bold text-gray-900 dark:text-white truncate paragraph-2">
                {getCustomerPhoneNumber() ? formatPhoneNumber(getCustomerPhoneNumber()!) : ''}
              </p>
              <Badge flavor={badgeProps.flavor} value={badgeProps.value} />
            </div>
          </div>

          <div className="text-gray-700 dark:text-gray-400 paragraph-2">
            {formatSecondsToTimeString(callDuration)}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CallListItem;
